import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { CATEGORY_LABELS, urgencyLabel, formatDueDate, OPERATION_STATES } from './operationStates';

/**
 * QuickAddComposer — capture a thought, hand it to Milo to parse.
 *
 * Two rendering modes:
 *   • Inline   (default) — embedded at the top of the OperationsQueue.
 *   • Modal              — when triggered globally via ⌘⇧A from outside
 *                          Event Readiness (rendered by AdminPage).
 *
 * Interaction:
 *   1. User types a one-liner.
 *   2. On change (debounced) we POST /operations/parse for Milo's read.
 *   3. The interpretation appears beneath the input.
 *   4. Enter or [Save] commits via POST /operations.
 *   5. Esc clears (modal mode also closes the modal).
 */
export default function QuickAddComposer({
  mode = 'inline',         // 'inline' | 'modal'
  onCreate,                // (data) => Promise — required
  onParse,                 // (text) => Promise<{ parsed }> — required
  onClose,                 // () => void, used in modal mode
  busy = false,
  autoFocus = false,
}) {
  const [text, setText] = useState('');
  const [parsed, setParsed] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const inputRef = useRef(null);
  const parseTimerRef = useRef(null);

  // Focus input on mount in modal mode, or when autoFocus is set
  useEffect(() => {
    if ((autoFocus || mode === 'modal') && inputRef.current) {
      const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 60);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [autoFocus, mode]);

  // Debounced parse
  useEffect(() => {
    if (parseTimerRef.current) clearTimeout(parseTimerRef.current);
    if (!text.trim() || !onParse) {
      setParsed(null);
      return undefined;
    }
    parseTimerRef.current = setTimeout(async () => {
      setParsing(true);
      try {
        const res = await onParse(text);
        if (res && res.parsed) setParsed(res.parsed);
      } catch {
        // Parse failures are non-fatal — host can still save raw title.
      } finally {
        setParsing(false);
      }
    }, 380);
    return () => clearTimeout(parseTimerRef.current);
  }, [text, onParse]);

  const reset = useCallback(() => {
    setText('');
    setParsed(null);
    setStatus('');
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!text.trim() || saving || busy) return;
    setSaving(true);
    setStatus('');
    const payload = parsed
      ? {
          title: parsed.title || text.trim(),
          state: parsed.state || 'active',
          urgency: parsed.urgency || 'this_week',
          category: parsed.category || 'other',
          due_date: parsed.due_date || null,
        }
      : { title: text.trim() };
    let res = null;
    try {
      res = await onCreate(payload);
    } catch (err) {
      res = null;
    }
    setSaving(false);
    if (res) {
      setStatus(`Added: "${payload.title}"`);
      reset();
      if (mode === 'modal' && typeof onClose === 'function') {
        // Close shortly so the user sees the confirmation
        setTimeout(() => onClose(), 250);
      }
    } else {
      setStatus('Could not save — try again.');
    }
  }, [text, parsed, saving, busy, onCreate, mode, onClose, reset]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (mode === 'modal' && typeof onClose === 'function') onClose();
      else reset();
    }
  };

  const composer = (
    <div className="qac" data-testid={`qac-${mode}`}>
      <div className="qac-row">
        <Sparkles className="qac-prefix" aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          className="qac-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Tell Milo what to carry — e.g. &ldquo;Need floral mockup by Thursday&rdquo;"
          disabled={busy || saving}
          data-testid="qac-input"
        />
        {mode === 'inline' && (
          <span className="qac-kbd" aria-hidden="true">⌘⇧A</span>
        )}
      </div>

      {parsed && (
        <div className="qac-preview">
          <div className="qac-preview-head">
            <Sparkles className="opq-icon-sm" aria-hidden="true" />
            <span>Milo will carry this for you</span>
            {parsing && <span style={{ marginLeft: 'auto', fontStyle: 'italic', textTransform: 'none', letterSpacing: 0 }}>re-reading…</span>}
          </div>
          <div className="qac-preview-rows">
            <div className="qac-preview-row">
              <span className="lbl">Title</span>
              <span className="val">{parsed.title}</span>
            </div>
            {parsed.due_date && (
              <div className="qac-preview-row">
                <span className="lbl">Due</span>
                <span className="val">{formatDueDate(parsed.due_date)} · {urgencyLabel(parsed.urgency)}</span>
              </div>
            )}
            {!parsed.due_date && (
              <div className="qac-preview-row">
                <span className="lbl">Timing</span>
                <span className="val">{urgencyLabel(parsed.urgency)}</span>
              </div>
            )}
            <div className="qac-preview-row">
              <span className="lbl">Category</span>
              <span className="val">{CATEGORY_LABELS[parsed.category] || parsed.category}</span>
            </div>
            <div className="qac-preview-row">
              <span className="lbl">State</span>
              <span className="val">{(OPERATION_STATES[parsed.state] || OPERATION_STATES.active).label}</span>
            </div>
          </div>
          <div className="qac-actions">
            <button type="button" className="qac-btn qac-btn-primary"
                    onClick={handleSubmit} disabled={saving || busy}
                    data-testid="qac-save">
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="qac-btn"
                    onClick={reset} disabled={saving || busy}>
              Discard
            </button>
          </div>
        </div>
      )}

      {!parsed && text.trim() && (
        <div className="qac-actions">
          <button type="button" className="qac-btn qac-btn-primary"
                  onClick={handleSubmit} disabled={saving || busy || !text.trim()}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="qac-btn" onClick={reset} disabled={saving || busy}>
            Discard
          </button>
        </div>
      )}

      {status && <div className="qac-status">{status}</div>}
    </div>
  );

  if (mode === 'modal') {
    return (
      <div
        className="qac-backdrop"
        onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
        role="presentation"
      >
        <div className="qac-modal" role="dialog" aria-modal="true" aria-label="Quick Add">
          <div className="qac-modal-head">
            <span className="qac-modal-title">Quick Add · Milo will read</span>
            <span className="qac-modal-esc">ESC</span>
          </div>
          {composer}
        </div>
      </div>
    );
  }

  return composer;
}
